import type { LiveDebugLogger } from '@/app/[locale]/(app)/vidu-stream/live-debug-log';

export const LIVE_RECONNECT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000] as const;

type ReconnectTimerId = ReturnType<typeof setTimeout>;

interface LiveReconnectorOptions {
  backoffMs?: readonly number[];
  clearTimer: (timerId: ReconnectTimerId) => void;
  hasNetworkAccess: () => boolean;
  logger?: LiveDebugLogger;
  onGiveUp: (message?: string) => void;
  onReconnect: () => void;
  onRecovering: (message?: string) => void;
  onWaitingForNetwork: (message?: string) => void;
  retryForever?: boolean;
  setTimer: (callback: () => void, delayMs: number) => ReconnectTimerId;
}

export class LiveReconnector {
  private readonly backoffMs: readonly number[];
  private readonly clearTimerFn: LiveReconnectorOptions['clearTimer'];
  private readonly hasNetworkAccess: LiveReconnectorOptions['hasNetworkAccess'];
  private readonly logger?: LiveDebugLogger;
  private readonly onGiveUp: LiveReconnectorOptions['onGiveUp'];
  private readonly onReconnect: LiveReconnectorOptions['onReconnect'];
  private readonly onRecovering: LiveReconnectorOptions['onRecovering'];
  private readonly onWaitingForNetwork: LiveReconnectorOptions['onWaitingForNetwork'];
  private readonly retryForever: boolean;
  private readonly setTimerFn: LiveReconnectorOptions['setTimer'];

  private attempt = 0;
  private isWaitingForNetworkState = false;
  private reconnectTimerId: ReconnectTimerId | null = null;

  constructor({
    backoffMs = LIVE_RECONNECT_BACKOFF_MS,
    clearTimer,
    hasNetworkAccess,
    logger,
    onGiveUp,
    onReconnect,
    onRecovering,
    onWaitingForNetwork,
    retryForever = false,
    setTimer,
  }: LiveReconnectorOptions) {
    this.backoffMs = backoffMs;
    this.clearTimerFn = clearTimer;
    this.hasNetworkAccess = hasNetworkAccess;
    this.logger = logger;
    this.onGiveUp = onGiveUp;
    this.onReconnect = onReconnect;
    this.onRecovering = onRecovering;
    this.onWaitingForNetwork = onWaitingForNetwork;
    this.retryForever = retryForever;
    this.setTimerFn = setTimer;
  }

  schedule(message?: string): void {
    if (!this.hasNetworkAccess()) {
      this.handleOffline(message);
      return;
    }

    if (this.reconnectTimerId !== null || this.isWaitingForNetworkState) {
      return;
    }

    if (!this.retryForever && this.attempt >= this.backoffMs.length) {
      this.logger?.log('reconnect', 'reconnect_give_up', { attempts: this.attempt });
      this.reset();
      this.onGiveUp(message);
      return;
    }

    this.onRecovering(message);

    const delayMs = this.backoffMs[this.attempt] ?? this.backoffMs[this.backoffMs.length - 1] ?? 0;
    const attemptSnapshot = this.attempt;
    this.attempt += 1;
    this.logger?.log('reconnect', 'reconnect_schedule', {
      attempt: attemptSnapshot,
      delay_ms: delayMs,
    });
    this.reconnectTimerId = this.setTimerFn(() => {
      this.reconnectTimerId = null;
      this.onReconnect();
    }, delayMs);
  }

  handleOffline(message?: string): void {
    if (this.reconnectTimerId !== null) {
      this.clearTimerFn(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }

    if (this.isWaitingForNetworkState) {
      return;
    }

    this.isWaitingForNetworkState = true;
    this.logger?.log('reconnect', 'reconnect_offline');
    this.onWaitingForNetwork(message);
  }

  handleOnline(): void {
    if (!this.isWaitingForNetworkState) {
      return;
    }

    this.isWaitingForNetworkState = false;
    this.attempt = 0;
    this.logger?.log('reconnect', 'reconnect_online');
    this.onReconnect();
  }

  notifySuccess(): void {
    this.reset();
  }

  stop(): void {
    this.reset();
  }

  hasPendingReconnect(): boolean {
    return this.reconnectTimerId !== null;
  }

  isWaitingForNetwork(): boolean {
    return this.isWaitingForNetworkState;
  }

  private reset(): void {
    this.attempt = 0;
    this.isWaitingForNetworkState = false;

    if (this.reconnectTimerId !== null) {
      this.clearTimerFn(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }
}
